import React from 'react';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  elevation?: 1 | 2 | 3 | 4 | 5;
  interactive?: boolean;
  onClick?: () => void;
  padding?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'default' | 'glass' | 'gradient' | 'outlined' | 'filled';
  glow?: boolean;
  hover?: 'lift' | 'glow' | 'scale' | 'none';
}

export function Card({ 
  children, 
  className = '', 
  elevation = 1, 
  interactive = false, 
  onClick,
  padding = 'md',
  variant = 'default',
  glow = false,
  hover = 'lift'
}: CardProps) {
  const paddingClasses = {
    none: '',
    xs: 'p-2',
    sm: 'p-3',
    md: 'p-4',
    lg: 'p-6',
    xl: 'p-8'
  };

  const variantClasses = {
    default: 'bg-surface-dark border border-surface-border',
    glass: 'bg-surface-dark/80 backdrop-blur-lg border border-white/10',
    gradient: 'bg-gradient-to-br from-surface-dark to-surface-raised border border-surface-border',
    outlined: 'bg-transparent border-2 border-surface-border',
    filled: 'bg-surface-raised border border-surface-high'
  };

  const hoverClasses = {
    lift: interactive ? 'hover:-translate-y-1 hover:scale-[1.02]' : '',
    glow: interactive ? 'hover:shadow-naia-glow' : '',
    scale: interactive ? 'hover:scale-105' : '',
    none: ''
  };

  const elevationClass = `elevation-${elevation}`;
  const hoverElevationClass = interactive && hover === 'lift' ? `hover:elevation-${Math.min(elevation + 1, 5)}` : '';
  const glowClass = glow ? 'naia-glow' : '';

  return (
    <div
      onClick={onClick}
      className={`
        rounded-xl transition-all duration-300 group relative overflow-hidden
        ${variantClasses[variant]}
        ${elevationClass} ${hoverElevationClass}
        ${hoverClasses[hover]}
        ${glowClass}
        ${interactive ? 'cursor-pointer hover:border-naia-pink/50' : ''}
        ${paddingClasses[padding]}
        ${className}
      `.trim()}
    >
      {/* Hover effect overlay */}
      {interactive && (
        <div className="absolute inset-0 bg-gradient-to-br from-naia-pink/5 to-cyan-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      )}
      
      {/* Shimmer effect on hover */}
      {interactive && variant === 'glass' && (
        <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/10 to-transparent pointer-events-none" />
      )}
      
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
}

interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
  actions?: React.ReactNode;
  subtitle?: string;
}

export function CardHeader({ children, className = '', actions, subtitle }: CardHeaderProps) {
  return (
    <div className={`border-b border-surface-border pb-4 mb-4 ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="text-lg font-semibold text-text-primary font-outfit">
            {children}
          </div>
          {subtitle && (
            <div className="text-sm text-text-secondary mt-1">
              {subtitle}
            </div>
          )}
        </div>
        {actions && (
          <div className="flex-shrink-0 ml-4">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}

interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export function CardContent({ children, className = '' }: CardContentProps) {
  return (
    <div className={`text-text-secondary ${className}`}>
      {children}
    </div>
  );
}

interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
  justify?: 'start' | 'center' | 'end' | 'between';
}

export function CardFooter({ children, className = '', justify = 'start' }: CardFooterProps) {
  const justifyClasses = {
    start: 'justify-start',
    center: 'justify-center',
    end: 'justify-end',
    between: 'justify-between'
  };

  return (
    <div className={`border-t border-surface-border pt-4 mt-4 flex items-center ${justifyClasses[justify]} ${className}`}>
      {children}
    </div>
  );
}

// Feature Card with enhanced visuals
interface FeatureCardProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  image?: string;
  action?: React.ReactNode;
  className?: string;
  featured?: boolean;
}

export function FeatureCard({ 
  title, 
  description, 
  icon, 
  image, 
  action, 
  className = '',
  featured = false 
}: FeatureCardProps) {
  return (
    <Card
      variant={featured ? 'gradient' : 'default'}
      interactive
      hover="lift"
      glow={featured}
      className={`group ${className}`}
    >
      {image && (
        <div className="relative mb-4 -m-4 mt-0 overflow-hidden rounded-t-xl">
          <img 
            src={image} 
            alt={title}
            className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-surface-dark/80 to-transparent" />
          {icon && (
            <div className="absolute bottom-4 left-4 p-3 bg-surface-dark/90 rounded-lg backdrop-blur-sm">
              <div className="text-naia-pink text-2xl">
                {icon}
              </div>
            </div>
          )}
        </div>
      )}
      
      {!image && icon && (
        <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-naia-pink/20 to-cyan-accent/20 rounded-lg mb-4 text-naia-pink text-2xl">
          {icon}
        </div>
      )}
      
      <CardHeader subtitle={description}>
        {title}
      </CardHeader>
      
      {action && (
        <CardFooter>
          {action}
        </CardFooter>
      )}
    </Card>
  );
}

// Stats Card for displaying metrics
interface StatsCardProps {
  title: string;
  value: string | number;
  change?: {
    value: number;
    type: 'increase' | 'decrease' | 'neutral';
  };
  icon?: React.ReactNode;
  className?: string;
}

export function StatsCard({ title, value, change, icon, className = '' }: StatsCardProps) {
  const changeColors = {
    increase: 'text-success',
    decrease: 'text-error',
    neutral: 'text-text-muted'
  };

  const changeSymbol = {
    increase: '+',
    decrease: '-',
    neutral: ''
  };

  return (
    <Card variant="glass" className={`text-center ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-text-muted text-sm font-medium">{title}</div>
        {icon && (
          <div className="text-naia-pink">
            {icon}
          </div>
        )}
      </div>
      
      <div className="text-3xl font-bold text-text-primary font-outfit mb-2">
        {value}
      </div>
      
      {change && (
        <div className={`text-sm ${changeColors[change.type]}`}>
          {changeSymbol[change.type]}{Math.abs(change.value)}%
        </div>
      )}
    </Card>
  );
} 